import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';

import api from '@/api/api';
import { titleOptions } from '@/constants/constants';
import {
  CREDIT_PROFILE_URL,
  DISPUTE_URL,
  FINANCIAL_ACCOUNT_URL,
  FULL_CREDIT_PROFILE_URL
} from '@/constants/urls';
import { CREDIT_REPORT_TOOLTIP_KEYS, LOG_PREFIX, TITLE_LABELS } from '@/types/enums';
import {
  AddressByIdResponse,
  AddressByPostcodeResponse,
  CRSearchHistoryItem,
  Dispute,
  DisputeFormatted,
} from '@/types/interfaces';
import {
  CreditProfileResponse,
  CreditReportAssetElement,
  CreditReportTooltip,
  CRFullGroupAccountDetails,
  FullCreditProfile,
} from '@/types/responseTypes';

interface State {
  addresses: AddressByPostcodeResponse[] | []
  addressInfo: AddressByIdResponse | null
  selectedSearchHistoryItem: CRSearchHistoryItem | null
  fullCreditProfile: FullCreditProfile | null
  tooltipModal: {
    isOpen: boolean
    selectedTooltip: CreditReportTooltip | null
  }
  isLoadingCreditReport: boolean
  isCreditReportError: boolean
  isDisabledAccount: boolean
  financialDetails: {
    data: CRFullGroupAccountDetails | null
    isLoadingFinancialDetails: boolean
    isFinancialDetailsError: boolean
  }
  possibleDisputes: DisputeFormatted[]
  selectedReportInsight: CreditReportAssetElement | null
}

interface NameParts {
  title: TITLE_LABELS | null
  forename: string | null
  surname: string | null
}

export default defineStore('creditReport', {
  state: (): State => ({
    addresses: [],
    addressInfo: null,
    selectedSearchHistoryItem: null,
    fullCreditProfile: null,
    tooltipModal: {
      isOpen: false,
      selectedTooltip: null,
    },
    isLoadingCreditReport: false,
    isCreditReportError: false,
    isDisabledAccount: false,
    financialDetails: {
      data: null,
      isLoadingFinancialDetails: false,
      isFinancialDetailsError: false,
    },
    possibleDisputes: [],
    selectedReportInsight: null,
  }),
  getters: {
    hasFullReport(state): boolean {
      return !!(state.fullCreditProfile?.report && state.fullCreditProfile.tooltips);
    },
    hasDashboard(state): boolean {
      return !!state.fullCreditProfile?.dashboard;
    },
    nameParts(state): NameParts {
      const returnObject: NameParts = {
        title: null,
        forename: null,
        surname: null,
      };

      const name = state.fullCreditProfile?.dashboard?.name;
      if (!name) return returnObject;

      const splitName = name.split(' ');

      const titleValues = titleOptions.map(title => title.label);
      const title = titleValues.includes(splitName[0] as TITLE_LABELS) ? splitName[0] : null;

      const forename = splitName[0] === title ? splitName[1] : splitName[0];

      let surname: string | null;
      if (title) {
        surname = splitName.length > 2 ? splitName[splitName.length - 1] : null;
      } else {
        surname = splitName.length > 1 ? splitName[splitName.length - 1] : null;
      }
      return {
        title: title as TITLE_LABELS || null,
        forename: forename || null,
        surname: surname || null,
      };
    },

  },
  actions: {
    openTooltipModal(tooltipKey: CREDIT_REPORT_TOOLTIP_KEYS) {
      if (this.fullCreditProfile && this.fullCreditProfile?.tooltips?.[tooltipKey]) {
        this.tooltipModal.selectedTooltip = this.fullCreditProfile.tooltips[tooltipKey];

        this.tooltipModal.isOpen = true;
      }
    },

    closeTooltipModal() {
      this.tooltipModal.isOpen = false;
    },

    updatePossibleDisputes(disputeCollection: Dispute[]): void {
      if (Array.isArray(disputeCollection) && disputeCollection.length > 0) {
        this.possibleDisputes = disputeCollection.map(({ name, type, info }) => ({
          name,
          body: {
            itemType: type,
            itemInformation: {
              ...info,
            },
          },
        }));
      }
    },

    applyCreditProfile(data: CreditProfileResponse): void {
      this.fullCreditProfile = {
        ...this.fullCreditProfile,
        account: data.account,
        dashboard: {
          ...this.fullCreditProfile?.dashboard,
          ...data.dashboard,
        },
        identificationOutcome: data.identificationOutcome,
        report: {
          ...this.fullCreditProfile?.report,
          ...data.report,
        },
        maxScore: data.maxScore,
      };
    },

    async getCreditProfile(): Promise<CreditProfileResponse> {
      try {
        this.isCreditReportError = false;
        this.isLoadingCreditReport = true;

        const { data } = await api<CreditProfileResponse>({
          url: CREDIT_PROFILE_URL,
          method: 'get',
          logPrefix: LOG_PREFIX.CREDIT_PROFILE,
        });

        this.applyCreditProfile(data);
        return data;
      } catch (error) {
        this.isCreditReportError = true;
        throw error;
      } finally {
        this.isLoadingCreditReport = false;
      }
    },

    async getFullCreditProfile(): Promise<void> {
      try {
        this.isCreditReportError = false;
        this.isLoadingCreditReport = true;

        const { data } = await api<FullCreditProfile>({
          url: FULL_CREDIT_PROFILE_URL,
          method: 'get',
          logPrefix: LOG_PREFIX.FULL_CREDIT_PROFILE,
        });

        this.fullCreditProfile = {
          ...this.fullCreditProfile,
          ...data,
        };
      } catch (e) {
        this.isCreditReportError = true;
      } finally {
        this.isLoadingCreditReport = false;
      }
    },

    async getDisputeUrl(
      disputeInfo: DisputeFormatted,
    ): Promise<AxiosResponse<{ body: string; url: string }>> {
      return api({
        url: DISPUTE_URL,
        method: 'post',
        data: {
          itemType: disputeInfo.body.itemType,
          itemInformation: disputeInfo.body.itemInformation || {},
        },
        logPrefix: LOG_PREFIX.DISPUTE,
      });
    },

    async getFinancialAccountDetails(accountID: string): Promise<void> {
      try {
        this.financialDetails.isLoadingFinancialDetails = true;

        const { data } = await api<CRFullGroupAccountDetails>({
          url: `${FINANCIAL_ACCOUNT_URL}/$financialAccountId`,
          method: 'get',
          pathParams: {
            $financialAccountId: accountID,
          },
          logPrefix: LOG_PREFIX.FINANCIAL_ACCOUNT,
        });

        this.financialDetails.data = data;
      } catch (e) {
        this.financialDetails.isFinancialDetailsError = true;
      } finally {
        this.financialDetails.isLoadingFinancialDetails = false;
      }
    },

    selectReportInsight(insight: CreditReportAssetElement): void {
      this.selectedReportInsight = insight;
    },
    closeReportInsight(): void {
      this.selectedReportInsight = null;
    },
  },
});

